import React, { useMemo, useState, useRef, useEffect } from "react";
import Head from "next/head";
import styles from "./GridGallery.module.scss";
import {
  ProductImages,
  getGalleryObjects,
  VideoPreloadValue,
  GalleryObjectType,
  SocialProofMessagesProperties,
} from "../utils/utils.ts";
import MediaMedium from "../components/Media/MediaMedium";
import { ZoomModal } from "../components/ZoomModal/ZoomModal";
import { Video } from "@/types/prodcatBaseTypes.ts";
import { imageProfileGenerator } from "@/utils";
import { CarouselGalleryUI } from "../CarouselGallery/CarouselGalleryUI";
import { SocialProofMessages } from "../components/SocialProofMessages/SocialProofMessages";
import { ErrorBoundary } from "@/utils/error/ErrorBoundary.tsx";
import { Experiences } from "@/getServerSideProps/monetate/getExperiences/experiencesSchema";
import classNames from "classnames/bind";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";
import { RuntimeConfig } from "@/types/types";

const cx = classNames.bind(styles);

type GridGalleryUIProps = {
  /**
   * A string representing the title of the gallery.
   */
  title: string;
  /**
   * An object representing the product images in the gallery.
   */
  images: ProductImages;
  /**
   * An array of Video objects representing the videos in the gallery.
   */
  videos: Video[];
  /**
   * A string representing the sizes available in the gallery.
   */
  sizes: string;
  /**
   * An optional flag passed from experience to show social proof messages
   */
  hasSocialProofMessagesExperience?: boolean;
  /**
   * An optional object representing options for Social Proof Messages
   */
  socialProofMessagesProperties: SocialProofMessagesProperties;
  hasSuperZoom?: boolean;
  /**
   * An optional boolean to indicate whether a high value for prefetching is enabled
   */
  usePrefetching?: boolean;
  useResponsiveImages?: boolean;
  pdpAsLanding: boolean;
  videoPreload: VideoPreloadValue;
  carouselGalleryInpTest?: boolean;
  galleryImageZoomOnHover?: boolean;
  experiencesRawObject: Experiences;
  runtimeConfig: RuntimeConfig;
};

export const GridGalleryUI = ({
  title,
  images,
  videos,
  sizes,
  hasSocialProofMessagesExperience = false,
  socialProofMessagesProperties,
  hasSuperZoom,
  usePrefetching,
  useResponsiveImages,
  pdpAsLanding,
  videoPreload,
  carouselGalleryInpTest = false,
  galleryImageZoomOnHover,
  experiencesRawObject,
  runtimeConfig,
}: GridGalleryUIProps) => {
  const [isZoomModalOpen, setZoomModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [spmOffset, setSpmOffset] = useState(0);
  const firstImageRef = useRef<HTMLDivElement>(null);

  const { flags } = useStandardProps();
  const useNewSocialProofMessagesPosition =
    flags.useNewSocialProofMessagesPosition?.value === true;

  const { images: galleryImages, videos: galleryVideos } = useMemo(
    () => getGalleryObjects(title, images, videos),
    [title, images, videos],
  );

  const mediaItems = [...galleryImages, ...galleryVideos];

  const openZoomModal = (
    event: React.MouseEvent<HTMLImageElement>,
    mediaSrc: string,
  ) => {
    const { target: media } = event;
    if (media) {
      setSelectedImageIndex(
        mediaItems.findIndex((go) =>
          go.type === GalleryObjectType.IMAGE
            ? go.imageUrl === mediaSrc
            : go.videoUrl === mediaSrc,
        ),
      );
      setZoomModalOpen(true);
    }
  };

  const closeZoomModal = () => {
    setZoomModalOpen(false);
  };

  const calculateSpmOffset = () => {
    if (
      window.matchMedia("(max-width: 1023px)").matches ||
      !firstImageRef.current
    ) {
      setSpmOffset(0);
      return;
    }

    setSpmOffset(
      Math.floor(firstImageRef.current.getBoundingClientRect().height - 100),
    );
  };

  const handleResize = () => {
    calculateSpmOffset();
  };

  useEffect(() => {
    if (!useNewSocialProofMessagesPosition) return;

    calculateSpmOffset();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section
      className={cx(styles.gridGalleryWrapper, {
        hideOnMobileAndTablet: pdpAsLanding,
      })}
      data-component="GridGallery"
    >
      <h2 className={styles.visuallyHidden}>Image gallery</h2>
      {hasSocialProofMessagesExperience && socialProofMessagesProperties && (
        <ErrorBoundary componentName="SocialProofMessages">
          <SocialProofMessages
            {...socialProofMessagesProperties}
            className={
              useNewSocialProofMessagesPosition
                ? "gridGalleryInstanceNewPosition"
                : "gridGalleryInstance"
            }
            offset={spmOffset}
          />
        </ErrorBoundary>
      )}
      <div data-testid="gallery" className={styles.gallery}>
        <Head>
          <link
            rel="preload"
            as="image"
            href={images.primary}
            imageSrcSet={imageProfileGenerator(images.primary)}
            imageSizes={sizes}
          />
        </Head>

        <MediaMedium
          openModal={openZoomModal}
          galleryObjects={[...galleryImages, ...galleryVideos]}
          usePrefetching={usePrefetching}
          limitImagesOnGrid={pdpAsLanding ? 2 : undefined}
          hideVideoModal={pdpAsLanding}
          disableFullWidthImageOnGrid={pdpAsLanding}
          videoPreload={videoPreload}
          carouselGalleryInpTest={carouselGalleryInpTest}
          galleryImageZoomOnHover={galleryImageZoomOnHover}
          ref={firstImageRef}
        />

        <ZoomModal
          closeZoomModal={closeZoomModal}
          mediaItems={[...galleryImages, ...galleryVideos]}
          selectedIndex={selectedImageIndex}
          setSelectedIndex={setSelectedImageIndex}
          hasSuperZoom={hasSuperZoom}
          videoPreload={videoPreload}
          carouselGalleryInpTest={carouselGalleryInpTest}
          videoAutoplayOnLoad={true}
          isZoomModalOpen={isZoomModalOpen}
        />
      </div>
      <div className={styles.gridGalleryCarousel}>
        <CarouselGalleryUI
          title={title}
          images={images}
          videos={videos}
          sizes={sizes}
          hasSuperZoom={hasSuperZoom}
          useResponsiveImages={useResponsiveImages}
          singleItemCarousel={pdpAsLanding}
          showVideosOnModal={pdpAsLanding}
          videoPreload={videoPreload}
          carouselGalleryInpTest={carouselGalleryInpTest}
          renderSection={false}
          experiencesRawObject={experiencesRawObject}
          runtimeConfig={runtimeConfig}
          socialProofMessagesProperties={socialProofMessagesProperties}
        />
      </div>
    </section>
  );
};
